// Box types catalogue - mirrors MakerCase structure.
//
// `occasions` is an OPTIONAL array of occasion slugs the tool fits.
// Used by the Home page "filter by occasion" chips. Tools without an
// occasion match every filter except a specific occasion search. The
// canonical list lives in OCCASIONS below.

// `tier` is the SINGLE SOURCE OF TRUTH for plan gating:
//   • 'free'  → available to every visitor (logged-in or not). Legacy
//               value 'basic' is treated as an alias for 'free' so old
//               catalogue entries keep working without migration.
//   • 'maker' → requires Maker, Pro or Team subscription (cascade)
//   • 'pro'   → requires Pro or Team subscription (cascade)
//   • 'team'  → requires Team subscription only
//   • 'draft' → admin-only (testing phase, hidden from public)
//
// Cascade rule: a tool tagged 'pro' is available to pro+team; 'maker'
// is available to maker+pro+team; 'free' is available to all. This is
// enforced server-side by deps.tier_allows() and mirrored on the
// frontend via tierIncludes() / userTierAllows() (see helpers below).
//
// The visual `tag` shown on home cards is auto-derived from `tier`
// (see helpers below), so flipping a tool's tier takes a single field
// edit and keeps the badge + the route guard + the admin override
// always in lockstep.
//
// `disabled: true` means the tool is not yet implemented and renders
// a "Coming soon" tooltip instead of a link.
// ─── Occasions ──────────────────────────────────────────────────────
//
// Catalogue of seasonal/event filters surfaced on the Home page. Each
// tile may declare `occasions: ['christmas', 'valentines', ...]` so the
// "filtrar por ocasião" chips on Home can narrow the grid to gift-ready
// projects. The list is intentionally short — too many chips dilutes
// the affordance. Each occasion carries its own accent color (used by
// the chip pill) and three locale strings.
//
// `month` is the typical Northern-hemisphere peak month (1-12). The
// Home page can auto-promote the occasion whose month is closest to
// today (e.g. December 1st → Christmas chip becomes the default).
export const OCCASIONS = [
  { id: 'christmas',   month: 12, accent: 'emerald',
    label: { pt: 'Natal',              en: 'Christmas',        es: 'Navidad' } },
  { id: 'valentines',  month: 2,  accent: 'rose',
    label: { pt: 'Dia dos Namorados',  en: "Valentine's Day",  es: 'San Valentín' } },
  { id: 'easter',      month: 4,  accent: 'amber',
    label: { pt: 'Páscoa',             en: 'Easter',           es: 'Pascua' } },
  { id: 'mothers-day', month: 5,  accent: 'fuchsia',
    label: { pt: 'Dia das Mães',       en: "Mother's Day",     es: 'Día de la Madre' } },
  { id: 'fathers-day', month: 6,  accent: 'sky',
    label: { pt: 'Dia dos Pais',       en: "Father's Day",     es: 'Día del Padre' } },
  { id: 'halloween',   month: 10, accent: 'amber',
    label: { pt: 'Halloween',          en: 'Halloween',        es: 'Halloween' } },
  { id: 'wedding',     month: 0,  accent: 'rose',
    label: { pt: 'Casamento',          en: 'Wedding',          es: 'Boda' } },
  { id: 'birthday',    month: 0,  accent: 'cyan',
    label: { pt: 'Aniversário',        en: 'Birthday',         es: 'Cumpleaños' } },
];

export const BOX_TYPES = [
  {
    id: 'basic',
    slug: 'basic',
    name: { en: 'Basic Box', pt: 'Caixa Básica' },
    tier: 'basic',
    category: 'boxes',
    featured: true,
    occasions: ['christmas', 'valentines', 'mothers-day', 'fathers-day', 'wedding', 'birthday'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'The classic wooden box. Pick the size, choose how the sides clip together and download the cut file.',
      pt: 'A caixa de madeira clássica. Escolha o tamanho, o tipo de encaixe e baixe o arquivo pra cortar.',
      es: 'La caja de madera clásica. Elegí el tamaño, cómo se unen las paredes y descargá el archivo para cortar.',
    },
  },
  {
    id: 'cardboard',
    slug: 'cardboard',
    name: { en: 'Cardboard Box', pt: 'Caixa de Papelão' },
    tier: 'basic',
    category: 'boxes',
    occasions: ['christmas', 'valentines', 'mothers-day', 'fathers-day', 'birthday'],
    kinds: ['Plotter', 'Laser Cut'],
    desc: {
      en: 'A folded cardboard box made from a single sheet — print, cut, fold and you’re done.',
      pt: 'Caixa de papelão dobrável feita de uma folha só — corta, vinca, dobra e pronto.',
      es: 'Caja de cartón plegable hecha de una sola lámina — cortás, marcás, doblás y listo.',
    },
  },
  {
    id: 'luminaire',
    slug: 'luminaire',
    name: { en: 'Parametric Luminaire', pt: 'Luminária Paramétrica' },
    tier: 'basic',
    category: 'luminaires',
    occasions: ['christmas', 'mothers-day', 'fathers-day', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A wooden lamp made of slats around a center disc — choose the height, slats and socket size.',
      pt: 'Luminária de madeira com ripas ao redor de um disco — escolha altura, número de ripas e o bocal.',
      es: 'Lámpara de madera con ripias alrededor de un disco — elegí altura, cantidad de ripias y portalámpara.',
    },
  },
  {
    id: 'luminaire-shapes',
    slug: 'luminaire-shapes',
    name: { en: 'Multi-Shape Luminaire', pt: 'Luminária Multi-Forma' },
    tier: 'basic',
    category: 'luminaires',
    occasions: ['christmas', 'mothers-day', 'fathers-day', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Same lamp, your shape: round, square, rectangle or polygon. Add a spiral twist if you want.',
      pt: 'A mesma luminária no formato que você quiser: redonda, quadrada, retangular ou poligonal. Com efeito espiral, se quiser.',
      es: 'La misma lámpara con la forma que quieras: redonda, cuadrada, rectangular o poligonal. Con efecto espiral, si te gusta.',
    },
  },
  {
    id: 'maze',
    slug: 'maze',
    name: { en: 'Maze Generator', pt: 'Gerador de Labirinto' },
    tier: 'basic',
    category: 'puzzles',
    kinds: ['Laser Cut', 'CNC', 'Plotter'],
    desc: {
      en: 'A maze on a board to play with marbles or just for decoration. Square or round, your size.',
      pt: 'Um labirinto numa placa pra brincar com bolinhas ou só pra enfeitar. Quadrado ou redondo, do seu tamanho.',
      es: 'Un laberinto en una tabla para jugar con bolitas o solo para decorar. Cuadrado o redondo, a tu medida.',
    },
  },
  {
    id: 'puzzle',
    slug: 'puzzle',
    name: { en: 'Jigsaw Puzzle', pt: 'Quebra-cabeças' },
    tier: 'basic',
    category: 'puzzles',
    occasions: ['christmas', 'birthday', 'easter'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A real jigsaw puzzle. Pick how many pieces and how irregular they look.',
      pt: 'Um quebra-cabeças de verdade. Escolha quantas peças e o quanto elas vão ser tortas.',
      es: 'Un rompecabezas de verdad. Elegí cuántas piezas y qué tan irregulares se ven.',
    },
  },
  {
    id: 'image-puzzle',
    slug: 'image-puzzle',
    name: { en: 'Photo Puzzle', pt: 'Puzzle de Imagem' },
    tier: 'pro',
    category: 'puzzles',
    occasions: ['valentines', 'mothers-day', 'fathers-day', 'birthday', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Upload a photo, turn it into a personalized jigsaw puzzle ready to cut and assemble.',
      pt: 'Mande uma foto e transforme em um quebra-cabeças personalizado pronto pra cortar e montar.',
      es: 'Subí una foto y conviértela en un rompecabezas personalizado listo para cortar y armar.',
    },
  },
  {
    id: 'vectorize',
    slug: 'vectorize',
    name: { en: 'Image Vectorizer', pt: 'Vetorizador de Imagens' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC', 'Plotter'],
    desc: {
      en: 'Got a logo or drawing? Turn the image into clean lines ready to cut on the laser.',
      pt: 'Tem um logo ou desenho? Transforme a imagem em linhas limpas prontas pra cortar no laser.',
      es: 'Tenés un logo o dibujo? Convierte la imagen en líneas limpias listas para cortar en el láser.',
    },
  },
  {
    id: 'hinge',
    slug: 'hinge',
    name: { en: 'Laser Hinge Box', pt: 'Caixa com Dobradiça a Laser' },
    tier: 'pro',
    category: 'boxes',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A wooden box with a flexible lid that bends — no extra hardware needed.',
      pt: 'Caixa de madeira com tampa que dobra sozinha — sem precisar de dobradiça extra.',
      es: 'Caja de madera con tapa que se dobla sola — sin necesidad de bisagras extra.',
    },
    disabled: true,
  },
  {
    id: 'grid',
    slug: 'grid',
    name: { en: 'Grid Divider Box', pt: 'Caixa com Divisórias em Grade' },
    tier: 'basic',
    category: 'boxes',
    occasions: ['christmas', 'mothers-day', 'fathers-day', 'birthday'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A wooden box with grid dividers inside — perfect to organize parts, tea or makeup.',
      pt: 'Caixa de madeira com divisórias em grade por dentro — boa pra organizar peças, chá ou maquiagem.',
      es: 'Caja de madera con divisiones en grilla por dentro — ideal para organizar piezas, té o maquillaje.',
    },
  },
  {
    id: 'divider',
    slug: 'divider',
    name: { en: 'Custom Divider Box', pt: 'Caixa com Divisórias Personalizadas' },
    tier: 'pro',
    category: 'boxes',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Design your own internal divider layout, slot by slot.',
      pt: 'Monte o layout interno do seu jeito, divisória por divisória.',
      es: 'Armá el layout interno a tu manera, división por división.',
    },
    disabled: true,
  },
  {
    id: 'slide',
    slug: 'slide',
    name: { en: 'Sliding Lid Box', pt: 'Caixa com Tampa Deslizante' },
    tier: 'pro',
    category: 'boxes',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A box with a sliding lid that goes in and out the side.',
      pt: 'Uma caixa com tampa que desliza pelo lado, tipo estojo.',
      es: 'Una caja con tapa que se desliza por el costado, estilo estuche.',
    },
    disabled: true,
  },
  {
    id: 'polygon',
    slug: 'polygon',
    name: { en: 'Polygon Box', pt: 'Caixa Poligonal' },
    tier: 'basic',
    category: 'boxes',
    occasions: ['christmas', 'birthday', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A box with 3 to 12 sides — triangle, hexagon, octagon, you name it.',
      pt: 'Uma caixa com 3 a 12 lados — triângulo, hexágono, octógono, você escolhe.',
      es: 'Una caja con 3 a 12 lados — triángulo, hexágono, octógono, vos elegís.',
    },
  },
  {
    id: 'cubby',
    slug: 'cubby',
    name: { en: 'Cubby Shelf', pt: 'Estante Nichos' },
    tier: 'basic',
    category: 'boxes',
    featured: true,
    occasions: ['christmas', 'mothers-day', 'fathers-day'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'An open shelving grid — rows × columns of cubbies, no top, no bottom. Pick row/column counts and even per-row sizes.',
      pt: 'Uma estante em grade — nichos em linhas × colunas, sem tampo e sem fundo. Escolha quantas linhas, colunas e até o tamanho de cada linha.',
      es: 'Una estante en grilla — nichos en filas × columnas, sin tapa ni fondo. Elegí filas, columnas y hasta el tamaño de cada fila.',
    },
  },
  {
    id: 'curved',
    slug: 'curved',
    name: { en: 'Curved Box', pt: 'Caixa Curvada' },
    tier: 'basic',
    category: 'boxes',
    occasions: ['christmas', 'valentines', 'mothers-day', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'A wooden box with rounded sides made by tiny laser cuts that let the wood bend.',
      pt: 'Caixa de madeira com lados curvos, feita com cortinhas que deixam a madeira dobrar.',
      es: 'Caja de madera con lados curvos, hecha con cortes finos que dejan que la madera se doble.',
    },
  },
  {
    id: 'panel',
    slug: 'panel',
    name: { en: 'Geometric Panel', pt: 'Painel Geométrico' },
    tier: 'basic',
    category: 'decoratives',
    occasions: ['christmas', 'wedding', 'easter'],
    kinds: ['Laser Cut', 'CNC', 'Plotter'],
    desc: {
      en: 'Decorative panels for the wall or partitions: mandalas, geometric patterns, latticework.',
      pt: 'Painéis decorativos pra parede ou divisória: mandalas, geometria, treliças.',
      es: 'Paneles decorativos para pared o divisor: mandalas, geometría, celosías.',
    },
  },
  {
    id: 'kerf',
    slug: 'kerf',
    name: { en: 'Kerf Calibration', pt: 'Calibração de Kerf' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Tiny test plate to find out exactly how much your laser eats off the wood. Helps every cut to fit right.',
      pt: 'Plaquinha de teste pra descobrir quanto seu laser come da madeira. Ajuda todos os encaixes a fechar certinho.',
      es: 'Placa de prueba para saber cuánto se come tu láser de la madera. Ayuda a que todos los encajes cierren bien.',
    },
  },
  {
    id: 'focus',
    slug: 'focus',
    name: { en: 'Laser Focus Gauge', pt: 'Gabarito de Foco do Laser' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut'],
    desc: {
      en: 'A small jig to find the perfect focus height of your laser — every level shows the height in numbers.',
      pt: 'Gabarito pequeno pra achar a altura ideal de foco do laser — cada degrau já vem com a medida marcada.',
      es: 'Plantilla chica para encontrar la altura ideal de foco del láser — cada escalón viene con la medida marcada.',
    },
  },
  {
    id: 'photo-dots',
    slug: 'photo-dots',
    name: { en: 'Halftone', pt: 'Halftone' },
    tier: 'basic',
    category: 'decoratives',
    occasions: ['valentines', 'mothers-day', 'fathers-day', 'birthday', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Turn any photo into a pattern of dots — fun for portraits, logos and landscapes ready to laser cut.',
      pt: 'Transforma qualquer foto em padrão de pontinhos — fica lindo em retratos, logos e paisagens pra cortar no laser.',
      es: 'Convierte cualquier foto en un patrón de puntitos — queda lindo en retratos, logos y paisajes para cortar al láser.',
    },
  },
  {
    id: 'ballot',
    slug: 'ballot',
    name: { en: 'Ballot Box', pt: 'Urna com Tampa' },
    tier: 'basic',
    category: 'boxes',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Wooden ballot box with a lockable lid — perfect for elections, raffles or suggestion drops.',
      pt: 'Urna de madeira com tampa que tranca — boa pra eleição, sorteio ou caixa de sugestões.',
      es: 'Urna de madera con tapa que cierra con candado — ideal para elecciones, sorteos o buzón de sugerencias.',
    },
  },
  {
    id: 'gear-builder',
    slug: 'gear-builder',
    name: { en: 'Gear Builder', pt: 'Gerador de Engrenagens', es: 'Generador de Engranajes' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Design involute spur gears — single or meshed pair. Adjust pitch, pressure angle, profile shift and export DXF/SVG ready to cut.',
      pt: 'Crie engrenagens cilíndricas com perfil involute — uma ou um par engrenado. Ajuste passo, ângulo de pressão e correção de perfil; exporta DXF/SVG pronto pra cortar.',
      es: 'Diseñá engranajes cilíndricos con perfil involute — uno o un par engranado. Ajustá paso, ángulo de presión y corrección de perfil; exporta DXF/SVG listo para cortar.',
    },
  },
  {
    id: 'bend-calculator',
    slug: 'bend-calculator',
    name: { en: 'Sheet Metal Bend Calculator', pt: 'Calculadora de Dobra de Chapa', es: 'Calculadora de Plegado de Chapa' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC', 'Plasma'],
    desc: {
      en: 'Sheet metal bend allowance, bend deduction and flat pattern — K-factor by material, R/T validation, DXF/SVG with fold lines ready for press brake.',
      pt: 'Calcula bend allowance, bend deduction e o desenvolvido (flat) de chapa dobrada — K-factor por material, valida R/T, exporta DXF/SVG com linhas de dobra prontas pra dobradeira.',
      es: 'Calculá bend allowance, bend deduction y el desarrollo plano de chapa plegada — K-factor por material, valida R/T, exporta DXF/SVG con líneas de plegado listas para plegadora.',
    },
  },
  {
    id: 'l-bracket',
    slug: 'l-bracket',
    name: { en: 'L-Bracket Generator', pt: 'Cantoneira em L', es: 'Cartabón en L' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC', 'Plasma'],
    desc: {
      en: 'Parametric L-bracket for sheet metal — pick material, thickness, leg lengths, hole pattern (round or slotted). Exports flat pattern with fold line in DXF/SVG, ready for laser/plasma and press brake.',
      pt: 'Cantoneira em L paramétrica pra chapa metálica — escolha material, espessura, pernas e padrão de furação (redondo ou slot). Exporta planificado com linha de dobra em DXF/SVG, pronto pra laser/plasma e dobradeira.',
      es: 'Cartabón en L paramétrico para chapa — elegí material, espesor, patas y patrón de agujeros (redondo o ranurado). Exporta el desarrollo con línea de plegado en DXF/SVG, listo para láser/plasma y plegadora.',
    },
  },
  {
    id: 'enclosure',
    slug: 'enclosure',
    name: { en: 'Electronic Enclosure', pt: 'Caixa Eletrônica', es: 'Caja Electrónica' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC', 'Plasma'],
    desc: {
      en: 'Parametric sheet-metal enclosure — U-bent body + lid + front/back panels with cutouts for USB, jacks, LEDs, buttons, RJ45, DB9 and IEC. Exports each piece as DXF/SVG with fold lines, ready for laser/plasma and press brake.',
      pt: 'Caixa eletrônica paramétrica em chapa — corpo em U + tampa + painéis frontal/traseiro com recortes pra USB, jacks, LEDs, botões, RJ45, DB9 e IEC. Exporta cada peça em DXF/SVG com linhas de dobra, pronto pra laser/plasma e dobradeira.',
      es: 'Caja electrónica paramétrica en chapa — cuerpo en U + tapa + paneles frontal/trasero con recortes para USB, jacks, LEDs, botones, RJ45, DB9 e IEC. Exporta cada pieza en DXF/SVG con líneas de plegado, lista para láser/plasma y plegadora.',
    },
  },
  {
    id: 'board-case',
    slug: 'board-case',
    name: { en: 'Board Case (Arduino / Raspberry Pi)', pt: 'Case para Placas (Arduino / Raspberry Pi)', es: 'Case para Placas (Arduino / Raspberry Pi)' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Parametric finger-joint enclosure for development boards — 16 presets (Arduino UNO R3/R4, Nano, Mega, Leonardo; Raspberry Pi 5/4B/3B+/Zero 2W; ESP32 DevKit, ESP8266 NodeMCU, BeagleBone Black). Auto port cutouts (USB, HDMI, Ethernet, GPIO), PCB screw holes, ventilation patterns. Pick clear/smoke acrylic or wood. Exports 6 pieces as DXF/SVG with nesting, ready for laser cut.',
      pt: 'Case paramétrico em finger-joint para placas de desenvolvimento — 16 presets (Arduino UNO R3/R4, Nano, Mega, Leonardo; Raspberry Pi 5/4B/3B+/Zero 2W; ESP32 DevKit, ESP8266 NodeMCU, BeagleBone Black). Recortes automáticos pra USB, HDMI, Ethernet e GPIO, furos pra parafuso da PCB, padrões de ventilação. Escolha acrílico transparente/fumê ou madeira. Exporta as 6 peças em DXF/SVG com nesting, pronto pra corte laser.',
      es: 'Case paramétrico en finger-joint para placas de desarrollo — 16 presets (Arduino UNO R3/R4, Nano, Mega, Leonardo; Raspberry Pi 5/4B/3B+/Zero 2W; ESP32 DevKit, ESP8266 NodeMCU, BeagleBone Black). Recortes automáticos para USB, HDMI, Ethernet y GPIO, agujeros para tornillo de la PCB, patrones de ventilación. Elegí acrílico transparente/ahumado o madera. Exporta las 6 piezas en DXF/SVG con nesting, listo para corte láser.',
    },
  },
  {
    id: 'display',
    slug: 'display',
    name: {
      en: 'Acrylic Display (Wall / Desk)',
      pt: 'Display de Acrílico (Parede / Mesa)',
      es: 'Display de Acrílico (Pared / Escritorio)',
    },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Parametric clear-acrylic display generator — four families (wall pocket, wall flat sleeve, L-stand desk, A-frame tent). Standard paper sizes (A2 / A3 / A4 / A5 / A6 / Letter) or custom. Optional thumb-cut for grabbing paper. Live 3D preview in transparent acrylic. Exports DXF / SVG with fold lines, ready for laser cut and strip-heater bending.',
      pt: 'Gerador paramétrico de expositores em acrílico transparente — quatro famílias (bolso de parede, porta-papel plano, suporte L de mesa, cavalete A). Tamanhos padrão A2/A3/A4/A5/A6/Carta ou customizado. Recorte opcional pro dedo. Preview 3D ao vivo em acrílico transparente. Exporta DXF/SVG com linhas de dobra, pronto pra corte laser e dobra com soldador de barra quente.',
      es: 'Generador paramétrico de displays en acrílico transparente — cuatro familias (bolsillo de pared, funda plana, soporte L de escritorio, caballete A). Tamaños estándar A2/A3/A4/A5/A6/Carta o personalizado. Recorte opcional para el dedo. Vista 3D en vivo en acrílico transparente. Exporta DXF/SVG con líneas de plegado, listo para corte láser y plegado con soldador de barra caliente.',
    },
  },
  {
    id: 'ai-dxf-gen',
    slug: 'ai-dxf-gen',
    name: { en: 'AI DXF Generator (Premium)', pt: 'Gerador IA — DXF Premium', es: 'Generador IA — DXF Premium' },
    tier: 'pro',
    category: 'ai-tools',
    kinds: ['Laser Cut', 'CNC', 'Plasma'],
    desc: {
      en: 'Describe a laser-cut design in plain language — the AI generates a 100% connected black-and-white image ready to be vectorised and exported as DXF/SVG. Trained for premium pierced metal plates: only two colours (black/white), no islands, no perspective, optimised for flat sheet with optional bending. Pro plan includes 50 generations per month.',
      pt: 'Descreva um desenho para corte a laser em linguagem natural — a IA gera uma imagem 100% conectada em preto e branco, pronta pra vetorizar e exportar em DXF/SVG. Treinada pra placas metálicas vazadas premium: só duas cores (preto/branco), sem ilhas, sem perspectiva, otimizada pra chapa plana com dobra opcional. Plano Pro inclui 50 gerações por mês.',
      es: 'Describe un diseño para corte láser en lenguaje natural — la IA genera una imagen 100% conectada en blanco y negro, lista para vectorizar y exportar como DXF/SVG. Entrenada para placas metálicas caladas premium: solo dos colores (blanco/negro), sin islas, sin perspectiva, optimizada para chapa plana con plegado opcional. Plan Pro incluye 50 generaciones por mes.',
    },
  },
  {
    id: 'rack-panel',
    slug: 'rack-panel',
    name: { en: 'Rack Panel (19"/10")', pt: 'Painel de Rack (19"/10")', es: 'Panel de Rack (19"/10")' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC', 'Plasma'],
    desc: {
      en: 'Parametric 19" EIA-310 and 10" SOHO rack panels — pick U height, mount hole type (round or cage-nut), and add custom cutouts (rect, round, slots) for displays, switches and connectors. Exports as DXF/SVG ready for laser/plasma or CNC.',
      pt: 'Painéis de rack 19" EIA-310 e 10" SOHO paramétricos — escolha a altura em U, tipo do furo de montagem (redondo ou porca-gaiola) e adicione cutouts (retangular, redondo ou slot) pra displays, switches e conectores. Exporta DXF/SVG pronto pra laser/plasma ou CNC.',
      es: 'Paneles de rack 19" EIA-310 y 10" SOHO paramétricos — elegí la altura en U, tipo de agujero de montaje (redondo o tuerca-jaula) y agregá cutouts (rectangulares, redondos o ranuras) para displays, switches y conectores. Exporta DXF/SVG listo para láser/plasma o CNC.',
    },
  },
  {
    id: 'slice-stack',
    slug: 'slice-stack',
    name: { en: 'Slice & Stack Wall Art', pt: 'Fatia & Empilha (Arte de Parede)', es: 'Corte & Apila (Arte Mural)' },
    tier: 'basic',
    category: 'tools',
    occasions: ['valentines', 'mothers-day', 'birthday', 'wedding', 'halloween'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Turn a 3D model into a stack of flat slices ready to laser/CNC-cut and assemble — like the bust and lips wall sculptures. Pick from a curated library (humans, animals, nature, decor, architectural) or use procedural shapes. Choose slice count, height, material thickness and fixation (pin-guide, slotted backboard or glue). Exports all numbered slices as DXF/SVG. 5 models free, 15 PRO.',
      pt: 'Transforma um modelo 3D em uma pilha de fatias planas prontas pra corte laser/CNC e montagem — como esculturas de busto e lábios em parede. Escolha de uma biblioteca curada (humanos, animais, natureza, decoração, arquitetura) ou use formas procedurais. Defina nº de fatias, altura, espessura do material e fixação (pinos-guia, backboard ranhurado ou cola). Exporta todas as fatias numeradas em DXF/SVG. 5 modelos grátis, 15 PRO.',
      es: 'Convierte un modelo 3D en una pila de cortes planos listos para láser/CNC y armado — como las esculturas murales de busto y labios. Elegí de una biblioteca curada (humanos, animales, naturaleza, decoración, arquitectura) o usá formas procedurales. Definí cantidad de cortes, altura, espesor del material y fijación (pin-guía, backboard ranurado o pegamento). Exporta todos los cortes numerados en DXF/SVG. 5 modelos gratis, 15 PRO.',
    },
  },
  // ── 3D Polygon — STL/OBJ → flat triangular pieces for metal sheet
  //    sculptures (Doberman/wolf/fox style). Phase-1 MVP: triangle-
  //    isolated unfold + grid layout + SVG export. Phase-2 will add
  //    strip grouping, joining tabs, DXF/PDF.
  {
    id: 'polygon-3d',
    slug: 'polygon-3d',
    name: { en: '3D Polygon', pt: '3D Polygon', es: '3D Polygon' },
    tier: 'basic',
    category: 'tools',
    kinds: ['Laser Cut', 'CNC', 'Plasma'],
    badge: 'new',
    desc: {
      en: 'Upload an STL/OBJ low-poly mesh and the tool unfolds every triangle as a flat piece on a single sheet — ready to laser/plasma-cut and weld/rivet into low-poly sheet-metal sculptures (Doberman, wolf, fox, geometric heads). Phase 1: isolated triangles, grid layout, SVG export. Phase 2 will add strip grouping, joining tabs, fold lines, DXF and paginated PDF.',
      pt: 'Suba um modelo STL/OBJ low-poly e a ferramenta desdobra cada triângulo como peça plana numa folha única — pronta pra corte laser/plasma e solda/rebite, montando esculturas de chapa low-poly (Doberman, lobo, raposa, cabeças geométricas). Fase 1: triângulos isolados, layout em grade, exportação SVG. Fase 2 adicionará agrupamento em tiras, abas de junção, linhas de dobra, DXF e PDF paginado.',
      es: 'Subí un modelo STL/OBJ low-poly y la herramienta despliega cada triángulo como pieza plana en una sola lámina — lista para corte láser/plasma y soldar/remachar, armando esculturas de chapa low-poly (Doberman, lobo, zorro, cabezas geométricas). Fase 1: triángulos aislados, disposición en grilla, exportación SVG. Fase 2 sumará agrupamiento en tiras, pestañas de unión, líneas de plegado, DXF y PDF paginado.',
    },
  },
  {
    id: 'fill-pattern',
    slug: 'fill-pattern',
    name: {
      en: 'Fill Pattern Panel',
      pt: 'Painel Perfurado',
      es: 'Panel Perforado',
    },
    tier: 'basic',
    category: 'tools',
    occasions: ['christmas', 'wedding'],
    kinds: ['Laser Cut', 'CNC'],
    desc: {
      en: 'Generate decorative perforated panels for laser/CNC — 8 patterns: Voronoi cells, Hilbert space-filling curve, honeycomb, lines, dots, waves, triangles and concentric rings. Pick any panel shape (rectangle, rounded-rect, circle, ellipse, hexagon), tune density and margins, and export DXF/SVG. Great for acoustic panels, ventilation grilles, dividers and wall lamps.',
      pt: 'Gera painéis decorativos perfurados pra laser/CNC — 8 padrões: células Voronoi, curva de Hilbert (space-filling), favo de mel, listras, pontos, ondas, triângulos e anéis concêntricos. Escolha a forma do painel (retângulo, retângulo arredondado, círculo, elipse, hexágono), ajuste densidade e margens, e exporte DXF/SVG. Excelente pra painéis acústicos, grelhas de ventilação, divisórias e luminárias de parede.',
      es: 'Genera paneles decorativos perforados para láser/CNC — 8 patrones: células Voronoi, curva de Hilbert (space-filling), panal, líneas, puntos, ondas, triángulos y anillos concéntricos. Elegí la forma del panel (rectángulo, rect. redondeado, círculo, elipse, hexágono), ajustá densidad y márgenes, y exportá DXF/SVG. Ideal para paneles acústicos, rejillas de ventilación, divisorias y lámparas de pared.',
    },
  },
  // ── Tool #18 — Connected Text (Step 3) ───────────────────────────────
  // Stencil cut-out lettering. Four geometric fonts (block/slab/round/
  // narrow) — all ship with built-in stencil bridges so the inner
  // counters of O, A, B, P, D, R never fall out when cut. Three output
  // modes: plate (text inside a board), cutout (loose letters), outline
  // (vector contours only). Trilingual.
  {
    id: 'connected-text',
    slug: 'connected-text',
    name: {
      en: 'Connected Text (Stencil)',
      pt: 'Texto Conectado (Stencil)',
      es: 'Texto Conectado (Stencil)',
    },
    tier: 'basic',
    category: 'signage',
    occasions: ['christmas', 'wedding', 'birthday', 'mothers-day', 'fathers-day'],
    kinds: ['Laser Cut', 'CNC'],
    badge: 'new',
    desc: {
      en: 'Cut-out lettering with built-in stencil bridges — counters of O, A, B, P, D stay attached automatically. 4 fonts (Block, Slab, Round, Narrow) and 3 modes: plate (text inside a board), loose letters, outline only. Trilingual.',
      pt: 'Letras recortáveis com pontes de stencil embutidas — os miolos de O, A, B, P, D ficam presos ao corpo automaticamente. 4 fontes (Block, Slab, Round, Narrow) e 3 modos: placa (texto no fundo), letras soltas, só contorno. Trilingue.',
      es: 'Letras recortables con puentes de stencil integrados — los miolos de O, A, B, P, D quedan unidos al cuerpo automáticamente. 4 fuentes (Block, Slab, Round, Narrow) y 3 modos: placa (texto en placa), letras sueltas, solo contorno. Trilingüe.',
    },
  },
  // ── Tool #19 — Single-Line Text (Step 3) ─────────────────────────────
  // Hershey-style stroke fonts (sans/script/gothic), each glyph is a
  // single polyline with no fill. Ideal for low-power laser engraving,
  // plotters/pen-bots, CNC v-carve. Supports multi-line text, align,
  // optional plate frame.
  {
    id: 'single-line-text',
    slug: 'single-line-text',
    name: {
      en: 'Single-Line Text',
      pt: 'Texto Single-Line',
      es: 'Texto Single-Line',
    },
    tier: 'basic',
    category: 'signage',
    occasions: ['birthday', 'wedding', 'mothers-day', 'fathers-day'],
    kinds: ['Laser Engrave', 'Plotter', 'CNC v-carve'],
    badge: 'new',
    desc: {
      en: 'Hershey-style single-line stroke text — each character is one polyline with no width. Three styles (Sans, Script, Gothic), multi-line support with alignment, optional plate frame. Perfect for low-power laser engraving, plotters/pen-bots and CNC v-carve.',
      pt: 'Texto Hershey single-line — cada caractere é uma polilinha sem largura. Três estilos (Sans, Script, Gothic), suporte multilinha com alinhamento, moldura de placa opcional. Ideal pra gravação a laser de baixa potência, plotters/pen-bots e v-carve em CNC.',
      es: 'Texto Hershey de una línea — cada carácter es una polilínea sin ancho. Tres estilos (Sans, Script, Gothic), soporte multilínea con alineación, marco de placa opcional. Perfecto para grabado láser de baja potencia, plotters/pen-bots y v-carve CNC.',
    },
  },
  // ── Tool #20 — Stir Stick (Step 3) ───────────────────────────────────
  // Drink/coffee stirrers with customisable decorative tops. 10 ready
  // shapes (circle, square, heart, star, leaf, flag, cocktail, cloud,
  // arrow, diamond), optional vertical engrave reusing the Hershey
  // engine from #19. Batch export up to 20 sticks per file.
  {
    id: 'stir-stick',
    slug: 'stir-stick',
    name: {
      en: 'Stir Sticks',
      pt: 'Palitos / Stir Sticks',
      es: 'Palillos / Stir Sticks',
    },
    tier: 'basic',
    category: 'tools',
    occasions: ['wedding', 'birthday', 'valentines', 'halloween', 'christmas'],
    kinds: ['Laser Cut', 'CNC'],
    badge: 'new',
    desc: {
      en: 'Stir sticks for drinks, coffee and tea with customisable decorative tops. 10 ready-made shapes (circle, square, heart, star, leaf, flag, cocktail, cloud, arrow, diamond), tip styles (square/pointed/rounded) and optional vertical engraving with Hershey single-line fonts. Batch up to 20 sticks per export.',
      pt: 'Palitos pra drinks, café e chá com topo decorativo customizável. 10 shapes prontos (círculo, quadrado, coração, estrela, folha, bandeirinha, taça martini, nuvem, seta, losango), estilos de ponta (reta/pontiaguda/arredondada) e gravação vertical opcional com fontes Hershey single-line. Lote de até 20 palitos por exportação.',
      es: 'Palillos para bebidas, café y té con topo decorativo personalizable. 10 formas listas (círculo, cuadrado, corazón, estrella, hoja, banderín, copa martini, nube, flecha, rombo), estilos de punta (recta/puntiaguda/redondeada) y grabado vertical opcional con fuentes Hershey de una línea. Lote de hasta 20 palillos por exportación.',
    },
  },
  // ── Tool #21 — Neon LED Sign Designer (Step 3) ───────────────────────
  // Single-line glue templates for 12V/24V LED neon flex tape. Each
  // letter/symbol is one continuous polyline that doubles as the
  // gluing guide on the backing plate. 8 fonts including 5 new
  // handwritten styles (cursive, casual, brush, signature, monoline)
  // plus pre-made symbols (heart, star, lightning, smiley, arrow,
  // infinity, music). Auto-quantizes total length to the profile step
  // (2.5cm/5cm/1.67cm), auto-splices closed letter loops, validates
  // bend radius per vertex, sizes power supply with 1.25 safety factor.
  {
    id: 'neon-sign',
    slug: 'neon-sign',
    name: {
      en: 'Neon LED Sign',
      pt: 'Letreiro Neon LED',
      es: 'Letrero Neón LED',
    },
    tier: 'basic',
    category: 'signage',
    occasions: ['wedding', 'birthday', 'valentines', 'christmas', 'mothers-day', 'fathers-day'],
    kinds: ['Laser Engrave', 'Plotter', 'CNC v-carve'],
    badge: 'new',
    desc: {
      en: 'Design single-line glue templates for 12V/24V LED neon flex tape. 8 font styles (3 geometric + 5 handwritten: cursive, casual, brush, signature, monoline) and 7 ready-made symbols (heart, star, lightning, smiley, arrow, infinity, music note). Auto-snaps total length to the profile step (2.5cm/5cm/1.67cm), auto-splices closed letter loops (B, O, P, 0, 8) with visible junction markers, validates minimum bend radius per vertex, sizes the power supply with 1.25 safety factor. Pink/cyan/green/amber/red/white/purple preview colors. Trilingual.',
      pt: 'Projete gabaritos single-line pra colar fita de neon LED flex 12V/24V. 8 estilos de fonte (3 geométricos + 5 manuscritos: cursiva, casual, brush, signature, monolínea) e 7 símbolos prontos (coração, estrela, raio, carinha, seta, infinito, nota musical). Auto-ajusta o comprimento ao step do perfil (2,5cm/5cm/1,67cm), auto-emenda loops fechados (B, O, P, 0, 8) com marcador visível, valida raio mínimo de curvatura por vértice e dimensiona a fonte com fator 1,25 de segurança. Cores de preview: rosa/azul/verde/âmbar/vermelho/branco/roxo. Trilingue.',
      es: 'Diseña plantillas single-line para pegar cinta de neón LED flex 12V/24V. 8 estilos de fuente (3 geométricos + 5 manuscritos: cursiva, casual, brush, signature, monolínea) y 7 símbolos listos (corazón, estrella, rayo, carita, flecha, infinito, nota musical). Ajusta automáticamente la longitud al paso del perfil (2,5cm/5cm/1,67cm), empalma automáticamente bucles cerrados (B, O, P, 0, 8) con marcador visible, valida el radio mínimo de curvatura por vértice y dimensiona la fuente con factor 1,25 de seguridad. Colores de preview: rosa/azul/verde/ámbar/rojo/blanco/morado. Trilingüe.',
    },
  },
  // ── Tool #22 — LED Pixel Board (drill template generator) ────────────
  // Multi-element drill template for classic Brazilian commercial signs
  // ("ABERTO", "CAIXA", "Café") made by press-fitting Φ5/8/10mm THT LEDs
  // through a panel along centerlines. Combine multiple texts, symbols
  // and uploaded logos on a single plate, each with its own LED color.
  // DXF export emits one LAYER per color (FUROS_VERMELHO, FUROS_VERDE,
  // FUROS_AZUL, FUROS_AMARELO, FUROS_BRANCO, FUROS_ROSA), plus the plate
  // outline (CONTORNO_PLACA). Supports discrete LEDs and matrix profiles
  // (P10/P16/P20) with locked pitch.
  {
    id: 'led-pixel-board',
    slug: 'led-pixel-board',
    name: {
      en: 'LED Pixel Board',
      pt: 'Placa LED',
      es: 'Placa LED',
    },
    tier: 'basic',
    category: 'tools',
    occasions: ['birthday', 'wedding', 'christmas'],
    kinds: ['Laser Cut', 'CNC', 'Drill template'],
    badge: 'new',
    desc: {
      en: 'Generate the drill template to manufacture LED pixel signs like "OPEN", "BAR", "CAFÉ" — the classic backlit panels you see on shop windows. Combine multiple texts, symbols and uploaded logos on a single plate, each with its own LED color (6-color palette). DXF export gives one layer per color (FUROS_VERMELHO, FUROS_VERDE, FUROS_AZUL, FUROS_AMARELO, FUROS_BRANCO, FUROS_ROSA), plus the plate outline. Supports Φ5/8/10mm discrete LEDs and matrix profiles (P10/P16/P20) with pitch-locked spacing. Optional border LEDs in any color.',
      pt: 'Gere o gabarito de furos para fabricar placas de LED estilo "ABERTO", "CAIXA", "Café" — aqueles letreiros retroiluminados das vitrines. Combine vários textos, símbolos e logos enviados na mesma placa, cada um com sua cor de LED (paleta de 6 cores). O DXF exporta uma camada por cor (FUROS_VERMELHO, FUROS_VERDE, FUROS_AZUL, FUROS_AMARELO, FUROS_BRANCO, FUROS_ROSA) + contorno da placa. Suporta LEDs Φ5/8/10mm e perfis matriz (P10/P16/P20) com espaçamento travado no pitch. LEDs de moldura opcionais em qualquer cor.',
      es: 'Genera la plantilla de perforación para fabricar placas de LED estilo "ABIERTO", "BAR", "CAFÉ" — los clásicos letreros retroiluminados de las vitrinas. Combina varios textos, símbolos y logos cargados en una sola placa, cada uno con su color de LED (paleta de 6 colores). El DXF exporta una capa por color (FUROS_VERMELHO, FUROS_VERDE, FUROS_AZUL, FUROS_AMARELO, FUROS_BRANCO, FUROS_ROSA) + contorno de la placa. Admite LEDs Φ5/8/10mm y perfiles matriz (P10/P16/P20) con espaciado bloqueado al pitch. LEDs de marco opcionales en cualquier color.',
    },
  },
  // ─── Categoria Áudio ─────────────────────────────────────────────────
  // Caixas acústicas e enclosures para alto-falantes. A primeira
  // ferramenta é uma caixa SELADA (closed-box) — o tipo mais simples
  // e o que cobre 80% dos usos (HiFi básico, automotivo, monitor de
  // estúdio). Bass-reflex e bandpass virão em iterações futuras.
  {
    id: 'speaker-sealed',
    slug: 'speaker-sealed',
    name: {
      en: 'Sealed Speaker Box',
      pt: 'Caixa Acústica Selada',
      es: 'Caja Acústica Sellada',
    },
    tier: 'basic',
    category: 'audio',
    occasions: [],
    kinds: ['Laser Cut', 'CNC'],
    badge: 'new',
    desc: {
      en: 'Design a sealed speaker enclosure from your driver Thiele-Small parameters. Pick from a curated list of popular drivers (Dayton Audio, JBL, Tang Band, Eminence, Pyle) or enter Vas/Qts/Fs manually. The tool calculates optimal internal volume (Vb), system resonance (Fc), F3 cutoff and box Qtc in real time, then generates 6 cut panels (front with driver hole, back, two sides, top, bottom) with finger-joint edges, mounting holes and the driver cutout. Dual-display: mm with inch hints for the US market.',
      pt: 'Projete uma caixa acústica selada a partir dos parâmetros Thiele-Small do seu alto-falante. Escolha entre uma lista curada de drivers populares (Dayton Audio, JBL, Tang Band, Eminence, Pyle) ou digite Vas/Qts/Fs manualmente. A ferramenta calcula o volume interno ótimo (Vb), ressonância do sistema (Fc), corte F3 e Qtc da caixa em tempo real, gerando 6 painéis (frente com furo do driver, traseira, duas laterais, topo, fundo) com encaixes finger-joint, furos de fixação e o recorte circular do alto-falante. Display duplo: mm com dica em polegadas para o mercado americano.',
      es: 'Diseña una caja acústica sellada a partir de los parámetros Thiele-Small de tu altavoz. Elegí de una lista curada de drivers populares (Dayton Audio, JBL, Tang Band, Eminence, Pyle) o ingresá Vas/Qts/Fs manualmente. La herramienta calcula el volumen interno óptimo (Vb), resonancia del sistema (Fc), corte F3 y Qtc de la caja en tiempo real, generando 6 paneles (frente con recorte del driver, trasera, dos laterales, tapa, fondo) con encajes finger-joint, agujeros de fijación y el recorte circular del altavoz. Display dual: mm con tip en pulgadas para el mercado estadounidense.',
    },
  },
  // Wall-Pocket Acrylic Display was merged into the main
  // /display-generator tool (as the "wall_pocket" family). The
  // standalone entry was removed to avoid a duplicate tile in the
  // tools catalogue.
  // ── Tool #23 — QR Code Vector Generator ──────────────────────────────
  // Vector QR Code generator for laser cut / CNC. Encodes anything:
  // URL, text, email, phone, SMS, WiFi credentials, vCard contact, geo
  // coordinates. 4 visual styles (squares, rounded, dots, connected),
  // 4 ECC levels (L/M/Q/H — 7/15/25/30% recovery), reserved logo area
  // for branded codes, optional backing plate with rounded corners.
  // Exports clean DXF (per-layer: outline / modules / backing) and SVG.
  {
    id: 'qrcode',
    slug: 'qrcode',
    name: {
      en: 'QR Code Vector',
      pt: 'QR Code Vetorial',
      es: 'QR Code Vectorial',
    },
    tier: 'basic',
    category: 'tools',
    occasions: ['wedding', 'birthday', 'christmas'],
    kinds: ['Laser Cut', 'CNC', 'Engrave'],
    badge: 'new',
    desc: {
      en: 'Vector QR Code generator for laser cutting and CNC engraving. Encode anything: URL, plain text, email, phone, SMS, WiFi credentials (auto-connect), vCard contact, geo coordinates. 4 visual styles (squares / rounded / dots / connected organic), 4 ECC levels (L/M/Q/H — 7-30% damage recovery), reserved central area for logo overlay, optional rounded backing plate. Clean DXF export with separate layers for outline / QR modules / backing — ready for tray production.',
      pt: 'Gerador de QR Code vetorial pra corte a laser e gravação CNC. Codifica qualquer coisa: URL, texto, email, telefone, SMS, credenciais WiFi (conexão automática), contato vCard, coordenadas geográficas. 4 estilos visuais (quadrados / arredondados / pontos / conectado orgânico), 4 níveis ECC (L/M/Q/H — recuperação de 7 a 30%), área central reservada pra logo, placa de fundo arredondada opcional. Exportação DXF limpa com camadas separadas pra contorno / módulos QR / base — pronto pra produção em bandeja.',
      es: 'Generador de QR Code vectorial para corte láser y grabado CNC. Codifica cualquier cosa: URL, texto, email, teléfono, SMS, credenciales WiFi (conexión automática), contacto vCard, coordenadas geográficas. 4 estilos visuales (cuadrados / redondeados / puntos / conectado orgánico), 4 niveles ECC (L/M/Q/H — recuperación del 7 al 30%), área central reservada para logo, placa de fondo redondeada opcional. Exportación DXF limpia con capas separadas para contorno / módulos QR / base — listo para producción en bandeja.',
    },
  },

  // ── Tool #24 — Tube & Profile Designer ───────────────────────────────
  // Inaugural tool of the new "Metalwork" category. Parametric designer
  // for tubular and structural profiles (round / square / rectangular /
  // oval) destined for tube-laser, fiber-laser and CNC bending shops.
  // The user picks a profile family, a stock size from DIN 2440 (metric)
  // or ASTM A500 (imperial), a length, a material (steel 1020, stainless
  // 304, aluminium 6061, brass CuZn37) and then layers features on top:
  // round holes, hole patterns, slots (rectangular cuts), centerline
  // bends (with min-bend-radius gating per material) and end cuts
  // (square / mitre / fish-mouth). The structural verdict panel reports
  // weight, perimeter, moment of inertia (Ix), section modulus (Wx) and
  // a slenderness check (L/r) so the operator can spot buckling risk
  // before the part hits the floor. Exports three files in one click:
  //   • STEP AP203 (ISO-10303-21) for TubePro / TubesT / Hymson /
  //     Fusion 360 / SolidWorks — the canonical solid for any tube-laser.
  //   • Binary STL for quick 3D preview and FDM/SLA prototyping.
  //   • DXF "unrolled" — cylindrical surface mapped to a flat sheet with
  //     OUTLINE / CUTS / ENGRAVE layers so a 2D laser-cut shop can
  //     produce the development without a tube-laser.
  {
    id: 'tubular-builder',
    slug: 'tubular-builder',
    name: {
      en: 'Tube & Profile Designer',
      pt: 'Projetor de Tubos e Perfis',
      es: 'Diseñador de Tubos y Perfiles',
    },
    tier: 'basic',
    category: 'metalwork',
    occasions: [],
    kinds: ['Tube Laser', 'Laser Cut', 'CNC'],
    badge: 'new',
    desc: {
      en: 'Parametric designer for tubular and structural profiles destined for tube-laser, fiber-laser and CNC bending shops. Pick a profile family (round / square / rectangular / oval), a stock size from DIN 2440 (metric) or ASTM A500 (imperial), a material (steel 1020, stainless 304, aluminium 6061, brass CuZn37) and layer features on top: round holes, hole patterns, slots, centerline bends (with material-specific minimum bend radius gating) and end cuts (square / mitre / fish-mouth). Structural verdict reports weight, perimeter, moment of inertia (Ix), section modulus (Wx) and slenderness (L/r) so you spot buckling risk before the part hits the floor. Exports STEP AP203 (TubePro / Fusion / SolidWorks), binary STL and DXF unrolled — three formats in one click.',
      pt: 'Projetor paramétrico de tubos e perfis estruturais pra tube-laser, laser de fibra e dobramento CNC. Escolhe a família de perfil (redondo / quadrado / retangular / oval), um tamanho de estoque DIN 2440 (métrico) ou ASTM A500 (imperial), um material (aço 1020, inox 304, alumínio 6061, latão CuZn37) e empilha features por cima: furos redondos, padrões de furos, rasgos, dobras de eixo (com raio mínimo travado por material) e cortes de ponta (reto / chanfro / boca-de-peixe). O parecer estrutural informa peso, perímetro, momento de inércia (Ix), módulo de resistência (Wx) e esbeltez (L/r) — você detecta risco de flambagem antes da peça ir pra produção. Exporta STEP AP203 (TubePro / Fusion / SolidWorks), STL binário e DXF desenrolado — três formatos num clique.',
      es: 'Diseñador paramétrico de tubos y perfiles estructurales para tube-laser, láser de fibra y plegado CNC. Escoge la familia de perfil (redondo / cuadrado / rectangular / ovalado), un tamaño de stock DIN 2440 (métrico) o ASTM A500 (imperial), un material (acero 1020, inoxidable 304, aluminio 6061, latón CuZn37) y apila features encima: agujeros redondos, patrones de agujeros, ranuras, plegados de eje (con radio mínimo bloqueado por material) y cortes de punta (recto / chaflán / boca de pez). El veredicto estructural informa peso, perímetro, momento de inercia (Ix), módulo de resistencia (Wx) y esbeltez (L/r) para detectar riesgo de pandeo antes de producir. Exporta STEP AP203 (TubePro / Fusion / SolidWorks), STL binario y DXF desenrollado — tres formatos en un clic.',
    },
  },
  // ─── Tube Nesting (1D cut plan / cutting stock) ──────────────────────
  //
  // Plans how to cut a list of pieces from standard bars/tubes with the
  // least scrap (First-Fit Decreasing 1D nesting). Groups by profile,
  // honours kerf, end trims and a fixed machine loss per bar, suggests
  // splices (emendas) for pieces longer than the bar, simulates cut time
  // from editable parameters, draws a bar diagram and exports a CSV plan
  // plus machine-ready STEP files (one per bar) bundled in a single .zip.
  {
    id: 'tube-nesting',
    slug: 'tube-nesting',
    name: {
      en: 'Tube Nesting — 1D Cut Plan',
      pt: 'Nesting de Tubos — Plano de Corte 1D',
      es: 'Nesting de Tubos — Plan de Corte 1D',
    },
    tier: 'basic',
    category: 'metalwork',
    occasions: [],
    kinds: ['Tube Laser', 'Laser Cut', 'CNC'],
    badge: 'new',
    desc: {
      en: 'Bar nesting (1D cutting-stock) for tubes and profiles: paste a cut list, set the bar length (default 3000 mm), kerf, end trims and a fixed machine loss per bar, and the optimiser packs the pieces with First-Fit Decreasing to minimise scrap. Groups by profile, suggests splices (emendas) for pieces longer than a bar, simulates cut time from editable parameters (cut speed, time per cut, bar load, setup), draws a per-bar diagram and exports a CSV plan plus machine-ready STEP files — one per bar — bundled in a single .zip.',
      pt: 'Nesting de barras (cutting-stock 1D) para tubos e perfis: cola sua lista de corte, define o comprimento da barra (padrão 3000 mm), a espessura de corte (kerf), as aparas de ponta e uma perda fixa de máquina por barra, e o otimizador encaixa as peças com First-Fit Decreasing pra reduzir o refugo. Agrupa por perfil, sugere emendas pra peças maiores que a barra, simula o tempo de corte a partir de parâmetros editáveis (velocidade de corte, tempo por corte, carga da barra, setup), desenha o diagrama de cada barra e exporta o plano em CSV mais arquivos STEP prontos pra máquina — um por barra — empacotados num único .zip.',
      es: 'Nesting de barras (cutting-stock 1D) para tubos y perfiles: pega tu lista de corte, define el largo de la barra (por defecto 3000 mm), el kerf, los recortes de punta y una pérdida fija de máquina por barra, y el optimizador acomoda las piezas con First-Fit Decreasing para reducir el desperdicio. Agrupa por perfil, sugiere empalmes para piezas más largas que la barra, simula el tiempo de corte con parámetros editables (velocidad de corte, tiempo por corte, carga de barra, setup), dibuja el diagrama de cada barra y exporta el plan en CSV más archivos STEP listos para máquina — uno por barra — empaquetados en un único .zip.',
    },
  },
  // ─── Parts Library (parametric metal parts) ──────────────────────────
  //
  // Library of parametric metal parts ported from the DesignCNC quote
  // system: base plates, brackets, flanges, rings, slots and gussets.
  // Pick a part, tune its parameters and see it live in 2D and 3D.
  // Single material (steel). Exports DXF/SVG with CUT and DRILL layers.
  {
    id: 'parts-library',
    slug: 'parts-library',
    name: {
      en: 'Parts Library',
      pt: 'Biblioteca de Peças',
      es: 'Biblioteca de Piezas',
    },
    tier: 'basic',
    category: 'metalwork',
    occasions: [],
    kinds: ['Laser Cut', 'Plasma', 'CNC', 'Router'],
    badge: 'new',
    desc: {
      en: 'Library of ready-made parametric metal parts for laser, plasma and router cutting: base plates (rectangular, 4-hole, center-hole, mounting, circular, rounded, chamfered), brackets (L, corner 90°, T, U, shelf), flanges (circular, square, blind), rings (washer, disc, C-ring), slots (single and double oblong) and gussets (triangular, trapezoid). Pick a part, tune its dimensions and hole pattern with live sliders, set the sheet thickness, and preview the part in 2D and in 3D (extruded steel) with live weight, net area and cut-length stats. Exports DXF/SVG with CUT and DRILL layers ready for the shop.',
      pt: 'Biblioteca de peças metálicas parametrizadas prontas para corte a laser, plasma e router: placas base (retangular, 4 furos, furo central, montagem, circular, arredondada, chanfrada), suportes (L, cantoneira 90°, T, U, mão francesa), flanges (circular, quadrada, cega), anéis (arruela, disco, anel C), oblongos (simples e duplo) e gussets (triangular, trapézio). Escolha a peça, ajuste dimensões e padrão de furos com sliders ao vivo, defina a espessura da chapa e veja a peça em 2D e em 3D (aço extrudado) com peso, área líquida e comprimento de corte calculados na hora. Exporta DXF/SVG com camadas CUT e DRILL prontas para a oficina.',
      es: 'Biblioteca de piezas metálicas paramétricas listas para corte láser, plasma y router: placas base (rectangular, 4 agujeros, agujero central, montaje, circular, redondeada, chaflanada), soportes (L, escuadra 90°, T, U, ménsula), bridas (circular, cuadrada, ciega), anillos (arandela, disco, anillo C), ranuras (simple y doble) y cartelas (triangular, trapecio). Elige la pieza, ajusta dimensiones y patrón de agujeros con sliders en vivo, define el espesor de la chapa y ve la pieza en 2D y en 3D (acero extruido) con peso, área neta y longitud de corte calculados al instante. Exporta DXF/SVG con capas CUT y DRILL listas para el taller.',
    },
  },
  // ─── Geodesic Dome Generator ─────────────────────────────────────────
  //
  // Parametric geodesic dome (Class-I icosahedron). Pick diameter,
  // frequency (ν 1–6), sphere fraction (3/8, 1/2, 5/8, full), strut tube
  // size, and place any number of door openings. Produces the strut cut
  // list grouped by length class (A, B, C…), the hub/connector summary by
  // arm count, the steel weight, and a live 3D view (struts coloured by
  // class, doors highlighted). Exports the cut list as CSV.
  {
    id: 'geodesic-dome',
    slug: 'geodesic-dome',
    name: {
      en: 'Geodesic Dome',
      pt: 'Domo Geodésico',
      es: 'Domo Geodésico',
    },
    tier: 'basic',
    category: 'metalwork',
    occasions: [],
    kinds: ['Laser Cut', 'Plasma', 'CNC', 'Router'],
    badge: 'new',
    desc: {
      en: 'Parametric geodesic dome generator (Class-I icosahedron). Set the diameter, frequency (ν 1–6), sphere fraction (3/8, 1/2, 5/8 or full), and the strut tube size, then add door openings anywhere around the base. The tool computes the full strut cut list grouped by length class (A, B, C…) with quantities, the connector (hub) summary by arm count (4, 5 and 6-way star nodes), the total linear metres and steel weight, and shows the dome live in 3D with struts coloured by class and doors highlighted. Exports the cut list as CSV for the shop. Steel.',
      pt: 'Gerador paramétrico de domos geodésicos (icosaedro classe I). Defina o diâmetro, a frequência (ν 1–6), a fração da esfera (3/8, 1/2, 5/8 ou cheia) e o tubo dos struts; adicione portas em qualquer ponto da base. A ferramenta calcula a lista de corte completa dos struts agrupada por classe de comprimento (A, B, C…) com quantidades, o resumo de conectores (hubs) por número de braços (nós estrela de 4, 5 e 6 vias), o total linear em metros e o peso em aço, e mostra o domo em 3D ao vivo com os struts coloridos por classe e as portas destacadas. Exporta a lista de corte em CSV para a oficina. Aço.',
      es: 'Generador paramétrico de domos geodésicos (icosaedro clase I). Define el diámetro, la frecuencia (ν 1–6), la fracción de esfera (3/8, 1/2, 5/8 o completa) y el tubo de las barras; añade puertas en cualquier punto de la base. La herramienta calcula la lista de corte completa agrupada por clase de longitud (A, B, C…) con cantidades, el resumen de conectores (hubs) por número de brazos (nodos estrella de 4, 5 y 6 vías), el total lineal en metros y el peso en acero, y muestra el domo en 3D en vivo con las barras coloreadas por clase y las puertas resaltadas. Exporta la lista de corte en CSV. Acero.',
    },
  },
  // ─── 3D Decorative Facade Panel (rout & fold) ────────────────────────
  //
  // Parametric generator for 3D decorative facade panels cut from flat
  // sheet (ACM, aluminium, MDF, acrylic). Five tessellating families —
  // diamond/pyramid, corrugated wave, gradient perforated screen (cobogó),
  // brise-soleil louver and hexagonal fish-scales — each with mountain/
  // valley fold creases and a live 3D relief preview. Built-in sheet
  // nesting tiles N copies onto a standard sheet and reports material
  // utilization. Exports DXF/SVG with per-feature CNC layers.
  {
    id: 'facade-panel',
    slug: 'facade-panel',
    name: {
      en: '3D Facade Panel',
      pt: 'Painel de Fachada 3D',
      es: 'Panel de Fachada 3D',
    },
    tier: 'basic',
    category: 'signage',
    occasions: [],
    kinds: ['Laser Cut', 'CNC', 'Router'],
    badge: 'new',
    desc: {
      en: 'Parametric generator for 3D decorative facade panels cut from flat sheet (ACM, aluminium, MDF, acrylic). Five tessellating pattern families: diamond/pyramid, corrugated wave, gradient perforated screen (cobogó), brise-soleil louver and hexagonal fish-scales. Each foldable pattern uses mountain/valley crease layers and a live 3D relief preview. Built-in sheet nesting tiles multiple copies onto a standard stock sheet (ACM 1220×2440 and more), computing parts per sheet, sheets needed and material utilization. Exports DXF/SVG with dedicated CUT, FOLD and DRILL layers.',
      pt: 'Gerador paramétrico de painéis decorativos de fachada 3D cortados de chapa plana (ACM, alumínio, MDF, acrílico). Cinco famílias de padrão tesselável: diamante/pirâmide, onda corrugada, cobogó perfurado com gradiente, brise-soleil (venezianas) e escamas hexagonais. Cada padrão dobrável usa camadas de vinco montanha/vale e preview 3D do relevo ao vivo. Nesting embutido encaixa várias cópias numa chapa padrão (ACM 1220×2440 e outras), calculando peças por chapa, chapas necessárias e aproveitamento de material. Exporta DXF/SVG com camadas dedicadas de CORTE, DOBRA e FURO.',
      es: 'Generador paramétrico de paneles decorativos de fachada 3D cortados de chapa plana (ACM, aluminio, MDF, acrílico). Cinco familias de patrón tesselable: diamante/pirámide, onda corrugada, celosía perforada con gradiente (cobogó), brise-soleil (lamas) y escamas hexagonales. Cada patrón plegable usa capas de pliegue montaña/valle y vista 3D del relieve en vivo. Nesting integrado acomoda varias copias en una chapa estándar (ACM 1220×2440 y más), calculando piezas por chapa, chapas necesarias y aprovechamiento del material. Exporta DXF/SVG con capas dedicadas de CORTE, PLIEGUE y AGUJERO.',
    },
  },
  // ─── Channel Letter (Letra Caixa) ────────────────────────────────────
  //
  // eCut-inspired channel-letter generator: face + unrolled return strip
  // (with fold tabs) + optional back + LED holes. Input from text+font OR
  // imported SVG. Materials: ACM (V-groove), galvanized (bend), acrylic
  // (glued). Front-lit / back-lit / both. 2D layout + 3D assembly preview.
  {
    id: 'channel-letter',
    slug: 'channel-letter',
    name: {
      en: 'Channel Letter (3D)',
      pt: 'Letra Caixa (3D)',
      es: 'Letra Corpórea (3D)',
    },
    tier: 'basic',
    category: 'signage',
    occasions: [],
    kinds: ['Laser Cut', 'CNC', 'Router'],
    badge: 'new',
    desc: {
      en: 'eCut-inspired channel-letter (3D dimensional letter) generator for storefront signage. Builds the FACE (front cut to the glyph), the RETURN (side wall unrolled to its true perimeter length, with corner bends and fold tabs), an optional BACK and auto-distributed LED module holes plus wiring exits. Input from typed text with built-in geometric fonts OR an imported SVG/logo. Three fabrication methods: ACM/Dibond rout-and-fold (V-groove return), galvanized folded sheet and glued acrylic strip. Front-lit, back-lit/halo or dual lighting. Live 2D nesting layout and a 3D assembly preview. Exports DXF/SVG with CUT, V_GROOVE/FOLD, DRILL and WIRE_HOLES layers.',
      pt: 'Gerador de letra caixa (letra 3D dimensional) inspirado no eCut para fachadas e comunicação visual. Monta a FACE (frente recortada no glifo), o RETURN (parede lateral desenrolada no comprimento real do perímetro, com dobras de canto e abas de fixação), um FUNDO opcional e furos de módulos de LED distribuídos automaticamente, além de saídas de fiação. Entrada por texto digitado com fontes geométricas embutidas OU por SVG/logo importado. Três métodos de fabricação: ACM/Dibond rout-and-fold (return com vinco em V), chapa galvanizada dobrada e tira de acrílico colada. Front-lit, back-lit/halo ou dupla iluminação. Planificação 2D ao vivo e preview de montagem 3D. Exporta DXF/SVG com camadas CUT, V_GROOVE/FOLD, DRILL e WIRE_HOLES.',
      es: 'Generador de letra corpórea (letra 3D) inspirado en eCut para fachadas y señalización. Construye la CARA (frente recortada del glifo), el RETURN (pared lateral desenrollada a su largo real, con dobleces de esquina y pestañas), un FONDO opcional y agujeros de módulos LED distribuidos automáticamente, más salidas de cableado. Entrada por texto con fuentes geométricas integradas O por SVG/logo importado. Tres métodos: ACM/Dibond rout-and-fold (return con ranura en V), chapa galvanizada doblada y tira de acrílico pegada. Front-lit, back-lit/halo o doble. Plano 2D en vivo y vista de montaje 3D. Exporta DXF/SVG con capas CUT, V_GROOVE/FOLD, DRILL y WIRE_HOLES.',
    },
  },
  // ─── Facade Frame (tubular sub-structure) ────────────────────────────
  //
  // Tubular skeleton (metalon) that sits behind an ACM/CV facade panel.
  // Takes the panel W×H manually and lays out a grid of vertical studs +
  // horizontal rails (+ optional perimeter) in square/rect/round tube,
  // producing a 1D-nested cut list, a DXF drilling map and a 3D preview
  // of the bars. Reuses the tube profile/stock catalogue.
  {
    id: 'facade-frame',
    slug: 'facade-frame',
    name: {
      en: 'Facade Frame',
      pt: 'Estrutura de Fachada',
      es: 'Estructura de Fachada',
    },
    tier: 'basic',
    category: 'signage',
    occasions: [],
    kinds: ['CNC', 'Welding', 'Metalwork'],
    badge: 'new',
    desc: {
      en: 'Tubular sub-structure (metalon skeleton) generator for the frame that sits behind an ACM/CV facade panel. Enter the panel width and height manually and the tool lays out a grid of vertical studs and horizontal rails, with an optional perimeter frame, in square, rectangular or round tube. It produces a 1D-nested cut list (quantity × length, bars needed and scrap), an auto-distributed DXF drilling map for the fixing holes and a 3D preview of the welded bars against a translucent panel reference. Steel, stainless and aluminium profiles with live weight estimate. Exports DXF/SVG with CUT, DRILL and MARKING_GUIDE layers.',
      pt: 'Gerador de subestrutura tubular (esqueleto de metalon) para a moldura que fica atrás de um painel de fachada ACM/comunicação visual. Informe a largura e a altura do painel manualmente e a ferramenta distribui uma grade de montantes verticais e travessas horizontais, com moldura de perímetro opcional, em tubo quadrado, retangular ou redondo. Gera uma lista de corte com aninhamento 1D (quantidade × comprimento, barras necessárias e sobra), um mapa de furação DXF distribuído automaticamente para os furos de fixação e um preview 3D das barras soldadas contra uma referência translúcida do painel. Perfis em aço, inox e alumínio com estimativa de peso ao vivo. Exporta DXF/SVG com camadas CUT, DRILL e MARKING_GUIDE.',
      es: 'Generador de subestructura tubular (esqueleto de metalon) para el marco que va detrás de un panel de fachada ACM/señalización. Introduce el ancho y alto del panel manualmente y la herramienta distribuye una rejilla de montantes verticales y travesaños horizontales, con marco perimetral opcional, en tubo cuadrado, rectangular o redondo. Produce una lista de corte con anidado 1D (cantidad × largo, barras necesarias y desperdicio), un mapa de perforación DXF distribuido automáticamente para los agujeros de fijación y una vista 3D de las barras soldadas contra una referencia translúcida del panel. Perfiles de acero, inox y aluminio con estimación de peso en vivo. Exporta DXF/SVG con capas CUT, DRILL y MARKING_GUIDE.',
    },
  },
  // ─── ACM Tray & Box (rout & fold V-groove) ───────────────────────────
  //
  // Parametric tray/box from aluminium composite (ACM/Dibond) using the
  // rout-and-fold method: a V-groove channel on the back lets the sheet
  // fold into 3D walls. Six ready models, lock-tab corners, DXF/SVG with
  // a dedicated V_GROOVE layer.
  {
    id: 'acm-tray',
    slug: 'acm-tray',
    name: {
      en: 'ACM Tray & Box',
      pt: 'Bandeja e Caixa ACM',
      es: 'Bandeja y Caja ACM',
    },
    tier: 'basic',
    category: 'signage',
    occasions: [],
    kinds: ['CNC', 'Router', 'Laser Cut'],
    badge: 'new',
    desc: {
      en: 'Rout-and-fold tray and box generator for aluminium composite (ACM / Dibond) and other foldable sheet. A V-groove channel milled on the back lets the flat blank fold into clean 3D walls — the classic signage and façade fabrication method. Includes six ready models, lock-tab (ear) corners for screwless assembly and editable wall height, flap and material thickness. Exports DXF/SVG with a dedicated V_GROOVE layer for the router plus CUT and DRILL layers.',
      pt: 'Gerador de bandejas e caixas pelo método rout-and-fold (fresa e dobra) para painel composto de alumínio (ACM / Dibond) e outras chapas dobráveis. Um canal em V fresado no verso permite dobrar o blank plano em paredes 3D limpas — o método clássico de fabricação de letreiros e fachadas. Inclui seis modelos prontos, cantos com orelha de trava (lock-tab) pra montagem sem parafusos e altura de parede, aba e espessura editáveis. Exporta DXF/SVG com camada V_GROOVE dedicada pro router mais camadas de CORTE e FURO.',
      es: 'Generador de bandejas y cajas por el método rout-and-fold (fresado y plegado) para panel compuesto de aluminio (ACM / Dibond) y otras chapas plegables. Un canal en V fresado en el dorso permite plegar el blank plano en paredes 3D limpias — el método clásico de fabricación de letreros y fachadas. Incluye seis modelos listos, esquinas con oreja de traba (lock-tab) para montaje sin tornillos y altura de pared, pestaña y espesor editables. Exporta DXF/SVG con capa V_GROOVE dedicada para el router más capas de CORTE y AGUJERO.',
    },
  },
  // ─── Logo to Neon Sign converter ─────────────────────────────────────
  //
  // Converts a logo/raster image into an LED-neon sign: vectorises the
  // outline, builds a single continuous neon path, backboard and mounting
  // holes. Exports DXF/SVG ready for flex-neon bending and acrylic cut.
  {
    id: 'logo-to-neon',
    slug: 'logo-to-neon',
    name: {
      en: 'Logo to Neon Sign',
      pt: 'Logo para Neon',
      es: 'Logo a Neón',
    },
    tier: 'basic',
    category: 'signage',
    occasions: [],
    kinds: ['Laser Cut', 'LED Neon', 'CNC'],
    badge: 'new',
    desc: {
      en: 'Turn a logo or raster image into an LED-neon sign. The tool vectorises the artwork, traces a continuous neon path, and generates the acrylic backboard with mounting and wire-exit holes. Tune stroke width, smoothing and scale, preview the lit result, and export DXF/SVG with NEON_PATH, BACKBOARD_CUT and MOUNTING_HOLES layers ready for flex-neon bending and acrylic cutting.',
      pt: 'Transforme um logo ou imagem em letreiro de neon LED. A ferramenta vetoriza a arte, traça um caminho de neon contínuo e gera a placa de fundo em acrílico com furos de fixação e saída de fio. Ajuste a espessura do traço, suavização e escala, veja o preview aceso e exporte DXF/SVG com camadas NEON_PATH, BACKBOARD_CUT e MOUNTING_HOLES prontas pra dobra de neon flexível e corte do acrílico.',
      es: 'Convierte un logo o imagen en un letrero de neón LED. La herramienta vectoriza el arte, traza un camino de neón continuo y genera la placa de fondo en acrílico con agujeros de fijación y salida de cable. Ajusta el grosor del trazo, suavizado y escala, previsualiza el resultado encendido y exporta DXF/SVG con capas NEON_PATH, BACKBOARD_CUT y MOUNTING_HOLES listas para doblar neón flexible y cortar el acrílico.',
    },
  },
];

// ─── Tier helpers ───────────────────────────────────────────────────────
//
// `getToolTier(slug)` reads from a runtime override map first (set by
// fetchToolTiers() once we hear back from /api/tools), otherwise falls
// back to the static catalogue. This lets an admin flip a tool's tier
// without redeploying the frontend.
//
// `requiresPro(slug)` is the canonical "should this tool be gated?"
// answer used everywhere (Home badge, route guard, sidebar hint).
//
// `getToolTag(slug, lang)` returns the localised badge label that
// should appear on the card — derived directly from the tier so the
// visual is ALWAYS in sync with the gating.

// Tier states (in order of visibility):
//   'draft' → admin-only, hidden from public catalogue (testing phase)
//   'basic' → free for everyone
//   'pro'   → requires an active Pro subscription
//
// `_tierOverrides` is the live map merged from /api/tools at boot. The
// public endpoint only sends 'draft' entries when the requester is an
// authenticated admin, so non-admins simply never learn that a draft
// exists for a slug — the catalogue's static tier is used for them.
// Map of slug -> { tier, enabled, sort_order, badge }, populated at
// boot from /api/tools. Plus a tiny pub/sub so React components that
// depend on this map re-render the moment overrides land — without
// this, the Home grid renders once with an empty map (drafts not
// filtered) and never updates when the API response arrives.
//
// Backward compatibility: applyToolTierOverrides() still accepts the
// legacy flat shape `{ slug: 'pro' }` so older clients/tests keep
// working. The richer shape `{ slug: { tier, enabled, ... } }` is
// preferred and used by the live API.
const _toolOverrides = new Map();
const _tierListeners = new Set();
// Bumped on every applyToolTierOverrides() call so React's
// useSyncExternalStore can invalidate cached selectors safely.
let _tierVersion = 0;

const VALID_BADGE = (b) =>
  (b === 'new' || b === 'exclusive' || b === 'launch' || b === 'soon') ? b : 'none';
const VALID_CATEGORY = (c) =>
  (c === 'boxes' || c === 'luminaires' || c === 'puzzles' || c === 'decoratives' || c === 'tools' || c === 'audio' || c === 'metalwork' || c === 'signage' || c === 'ai-tools') ? c : null;

// Accepted tier values from the API (after the 4-tier rollout). Legacy
// 'basic' is normalised to 'free' so the rest of the codebase only has
// to deal with the canonical vocabulary {free, maker, pro, team, draft}.
const _normalizeTier = (t) => {
  if (t === 'basic' || t === 'free') return 'free';
  if (t === 'maker') return 'maker';
  if (t === 'pro') return 'pro';
  if (t === 'team') return 'team';
  if (t === 'draft') return 'draft';
  return 'free';
};

export function applyToolTierOverrides(map) {
  _toolOverrides.clear();
  if (map) {
    for (const [slug, value] of Object.entries(map)) {
      if (typeof value === 'string') {
        // Legacy shape — treat as tier-only override, defaults for the rest.
        const tier = _normalizeTier(value);
        _toolOverrides.set(slug, {
          tier, enabled: true, sort_order: 1000, badge: 'none',
          category: null, featured: false,
        });
      } else if (value && typeof value === 'object') {
        const tier = _normalizeTier(value.tier);
        _toolOverrides.set(slug, {
          tier,
          enabled: value.enabled !== false,
          sort_order: Number.isFinite(value.sort_order) ? value.sort_order : 1000,
          badge: VALID_BADGE(value.badge),
          category: VALID_CATEGORY(value.category),
          featured: value.featured === true,
        });
      }
    }
  }
  _tierVersion += 1;
  for (const fn of _tierListeners) {
    try { fn(_tierVersion); } catch (_) { /* noop */ }
  }
}

export function subscribeToolTiers(fn) {
  _tierListeners.add(fn);
  return () => _tierListeners.delete(fn);
}

export function getToolTierVersion() {
  return _tierVersion;
}

// ─── Categories store ──────────────────────────────────────────────
//
// Home-page categories are now DYNAMIC data fetched from
// GET /api/categories (source of truth: the backend `categories`
// collection). This store mirrors the tool-tier store: applyCategories()
// replaces the live list, subscribeCategories() lets React invalidate
// via useSyncExternalStore, and getCategories() returns the ordered
// list. If the API is unreachable we fall back to DEFAULT_CATEGORIES so
// the Home page always renders (no white screen).
//
// Each category: { id, label:{pt,en,es}, icon, accent, preview,
//                  sort_order, enabled, builtin }
// `icon` is a Lucide icon NAME (string) resolved to a component in
// Home.jsx via an icon map (with a generic fallback).

export const DEFAULT_CATEGORIES = [
  { id: 'boxes',       icon: 'Package',   accent: 'cyan',    preview: '/preview/basic-box.webp',
    label: { pt: 'Caixas', en: 'Boxes', es: 'Cajas' }, sort_order: 10, enabled: true, builtin: true },
  { id: 'luminaires',  icon: 'Lightbulb', accent: 'amber',   preview: '/preview/luminaire.webp',
    label: { pt: 'Luminárias', en: 'Luminaires', es: 'Luminarias' }, sort_order: 20, enabled: true, builtin: true },
  { id: 'puzzles',     icon: 'Puzzle',    accent: 'fuchsia', preview: '/preview/halftone.webp',
    label: { pt: 'Quebra-cabeças', en: 'Puzzles', es: 'Rompecabezas' }, sort_order: 30, enabled: true, builtin: true },
  { id: 'decoratives', icon: 'Palette',   accent: 'emerald', preview: '/preview/grid-divider-box.webp',
    label: { pt: 'Decorativos', en: 'Decoratives', es: 'Decorativos' }, sort_order: 40, enabled: true, builtin: true },
  { id: 'audio',       icon: 'Volume2',   accent: 'violet',  preview: '/preview/basic-box.webp',
    label: { pt: 'Áudio', en: 'Audio', es: 'Audio' }, sort_order: 50, enabled: true, builtin: true },
  { id: 'tools',       icon: 'Wrench',    accent: 'sky',     preview: '/preview/luminaire-shapes.webp',
    label: { pt: 'Ferramentas', en: 'Tools', es: 'Herramientas' }, sort_order: 60, enabled: true, builtin: true },
  { id: 'metalwork',   icon: 'Hammer',    accent: 'orange',  preview: '/preview/basic-box.webp',
    label: { pt: 'Metalurgia', en: 'Metalwork', es: 'Metalurgia' }, sort_order: 70, enabled: true, builtin: true },
  { id: 'signage',     icon: 'Megaphone', accent: 'rose',    preview: '/preview/luminaire.webp',
    label: { pt: 'Sinalização', en: 'Signage', es: 'Señalización' }, sort_order: 80, enabled: true, builtin: true },
];

// Fallback bucket every orphan tool is grouped under.
export const FALLBACK_CATEGORY = 'tools';

let _categories = DEFAULT_CATEGORIES.slice();
let _categoriesLoaded = false;
let _catVersion = 0;
const _catListeners = new Set();

// Replace the live category list (called at boot from the API payload).
// Empty / malformed input keeps the current defaults so a transient API
// hiccup never blanks the Home page.
export function applyCategories(list) {
  if (Array.isArray(list) && list.length) {
    _categories = list
      .filter((c) => c && c.id)
      .map((c) => ({
        id: c.id,
        label: c.label || { pt: c.id, en: c.id, es: c.id },
        icon: c.icon || 'Shapes',
        accent: c.accent || 'cyan',
        preview: c.preview || '/preview/basic-box.webp',
        sort_order: Number.isFinite(c.sort_order) ? c.sort_order : 1000,
        enabled: c.enabled !== false,
        builtin: c.builtin === true,
      }))
      .sort((a, b) => a.sort_order - b.sort_order);
    _categoriesLoaded = true;
  }
  _catVersion += 1;
  for (const fn of _catListeners) {
    try { fn(_catVersion); } catch (_) { /* noop */ }
  }
}

export function subscribeCategories(fn) {
  _catListeners.add(fn);
  return () => _catListeners.delete(fn);
}

export function getCategoriesVersion() {
  return _catVersion;
}

// Ordered list of ENABLED categories (for the public Home grid).
export function getCategories() {
  return _categories.filter((c) => c.enabled !== false);
}

// Full list incl. disabled (for the admin panel local mirror).
export function getAllCategories() {
  return _categories.slice();
}

export function categoriesLoaded() {
  return _categoriesLoaded;
}

// Localised label for a category id, with graceful fallbacks.
export function getCategoryLabel(id, lang = 'pt') {
  const c = _categories.find((x) => x.id === id);
  if (!c) return id;
  return (c.label && (c.label[lang] || c.label.pt || c.label.en)) || id;
}

// Read full override record (or null if no override exists).
export function getToolOverride(slug) {
  return _toolOverrides.get(slug) || null;
}

export function getToolTier(slug) {
  const o = _toolOverrides.get(slug);
  if (o) return o.tier;
  const t = BOX_TYPES.find((b) => b.slug === slug);
  return _normalizeTier(t?.tier);
}

// Cascade rule (mirrors backend deps.tier_allows / TIER_ORDER):
//   anon < free < maker < pro < team < admin
// A higher-rank user automatically gets access to lower-tier tools.
// `userTierAllows('pro', 'team')` → true (team includes pro)
// `userTierAllows('pro', 'maker')` → false (maker can't reach pro tools)
const TIER_RANK = { anon: 0, free: 1, maker: 2, pro: 3, team: 4, admin: 5 };

export function userTierAllows(requiredTier, userTier) {
  // 'free'/'basic' tools are open to EVERY visitor, including anonymous
  // ones (see the catalogue comment: "'free' → available to every visitor,
  // logged-in or not"). Without this, an anon (rank 0) fails the 0 >= 1
  // check for a free tool (rank 1) and wrongly sees the upgrade/paywall
  // modal — which is exactly what happened on the free Tube Designer.
  const req = _normalizeTier(requiredTier);
  if (req === 'free') return true;
  const need = TIER_RANK[req];
  const have = TIER_RANK[userTier];
  if (need === undefined) return true;          // unknown requirement = open
  if (have === undefined) return false;         // unknown user = block
  return have >= need;
}

// Which subscription tiers grant access to a tool tagged with `tier`.
// Returns the canonical list in display order (lowest plan that
// unlocks first). 'draft' is admin-only and returns an empty list.
export function tierIncludes(tier) {
  const t = _normalizeTier(tier);
  if (t === 'draft') return [];
  if (t === 'free')  return ['free', 'maker', 'pro', 'team'];
  if (t === 'maker') return ['maker', 'pro', 'team'];
  if (t === 'pro')   return ['pro', 'team'];
  if (t === 'team')  return ['team'];
  return ['free', 'maker', 'pro', 'team'];
}

export function requiresPro(slug) {
  // Backwards compatible: returns true whenever the tool needs ANY
  // paid plan (maker / pro / team). Older call sites only used this
  // for "show upgrade modal?" decisions, so promoting maker/team to
  // also trigger the gate is the safe behaviour.
  const tier = getToolTier(slug);
  return tier === 'maker' || tier === 'pro' || tier === 'team';
}

export function isDraft(slug) {
  return getToolTier(slug) === 'draft';
}

// Whether the tool is enabled in the public catalogue. Admins always
// see disabled tools (so they can re-enable them); everyone else gets
// them stripped by visibleBoxTypes().
export function isToolEnabled(slug) {
  const o = _toolOverrides.get(slug);
  if (o) return !!o.enabled;
  return true;            // catalogue default = enabled
}

// Visual badge layered on top of the Free/Pro pill. Returns the bare
// machine value ('none' | 'new' | 'exclusive'); use getToolBadgeLabel()
// for the localized text.
export function getToolBadge(slug) {
  const o = _toolOverrides.get(slug);
  return o ? o.badge : 'none';
}

export function getToolBadgeLabel(slug, lang = 'pt') {
  const b = getToolBadge(slug);
  if (b === 'new')       return lang === 'en' ? 'New'       : (lang === 'es' ? 'Nuevo'      : 'Novo');
  if (b === 'launch')    return lang === 'en' ? 'Launch'    : (lang === 'es' ? 'Lanzamiento': 'Lançamento');
  if (b === 'exclusive') return lang === 'en' ? 'Exclusive' : (lang === 'es' ? 'Exclusivo'  : 'Exclusivo');
  if (b === 'soon')      return lang === 'en' ? 'Coming soon' : (lang === 'es' ? 'Próximamente' : 'Em breve');
  return null;
}

// True when a tool's badge marks it as "coming soon" (preview-only on
// Home: visible, but the card must NOT be clickable). Catalogue-level
// `disabled: true` is also treated as soon for backward compatibility.
export function isToolComingSoon(slug) {
  if (getToolBadge(slug) === 'soon') return true;
  const t = BOX_TYPES.find((b) => b.slug === slug);
  return !!t?.disabled;
}

// Effective sort position. Returns the override's value when present,
// otherwise the catalogue index × 100 so static order is preserved
// while leaving room for admin tweaks between tools.
export function getToolSortOrder(slug) {
  const o = _toolOverrides.get(slug);
  if (o && Number.isFinite(o.sort_order)) return o.sort_order;
  const idx = BOX_TYPES.findIndex((b) => b.slug === slug);
  return idx >= 0 ? (idx + 1) * 100 : 999_999;
}

// Effective occasions list for a tool. Reads override first (so admins
// can tag a tool from the panel) and falls back to the catalogue
// `occasions` array. Always returns an array (possibly empty) so the
// caller can `.includes()` safely.
export function getToolOccasions(slug) {
  const o = _toolOverrides.get(slug);
  if (o && Array.isArray(o.occasions)) return o.occasions;
  const t = BOX_TYPES.find((b) => b.slug === slug);
  return Array.isArray(t?.occasions) ? t.occasions : [];
}

// Filter a list of tiles by an occasion id. Returns tiles tagged with
// the occasion. `occasionId === null` or `'all'` returns the input as-is.
export function filterByOccasion(boxes, occasionId) {
  if (!occasionId || occasionId === 'all') return boxes;
  return boxes.filter((b) => getToolOccasions(b.slug).includes(occasionId));
}

// Effective category: admin override wins, falls back to the catalogue
// `category` field, finally defaults to the fallback bucket. If the
// resolved category no longer exists (deleted by an admin), the tool is
// re-homed to the fallback so it never disappears from the Home grid.
export function getToolCategory(slug) {
  const o = _toolOverrides.get(slug);
  const t = BOX_TYPES.find((b) => b.slug === slug);
  let cat = (o && o.category) || t?.category || FALLBACK_CATEGORY;
  // Re-home orphans only once categories have actually loaded from the
  // API (before that, _categories is the full default set anyway).
  if (_categoriesLoaded && !_categories.some((c) => c.id === cat)) {
    cat = FALLBACK_CATEGORY;
  }
  return cat;
}

// True when this tool carries the "Mais usada" star (only ONE tool can
// be featured at a time — the backend enforces this invariant on PATCH).
// Admin override wins; falls back to the catalogue `featured` flag.
export function isToolFeatured(slug) {
  const o = _toolOverrides.get(slug);
  if (o && typeof o.featured === 'boolean') return o.featured;
  const t = BOX_TYPES.find((b) => b.slug === slug);
  return !!t?.featured;
}

// Filter the catalogue for a given viewer + sort by effective order.
// Admins see everything (drafts + disabled) so the Admin Panel can
// manage them; everyone else has drafts and disabled tools stripped.
export function visibleBoxTypes(isAdmin) {
  const list = isAdmin
    ? BOX_TYPES
    : BOX_TYPES.filter((b) => !isDraft(b.slug) && isToolEnabled(b.slug));
  return [...list].sort(
    (a, b) => getToolSortOrder(a.slug) - getToolSortOrder(b.slug),
  );
}

export function getToolTag(slug, lang = 'pt') {
  const tier = getToolTier(slug);
  // Plan-name pills are kept untranslated across locales (brand style),
  // so the same word reads the same on the home grid, the pricing page
  // and the upgrade modal.
  if (tier === 'pro')   return 'Pro';
  if (tier === 'maker') return 'Maker';
  if (tier === 'team')  return 'Team';
  if (tier === 'draft') return lang === 'en' ? 'Draft' : 'Rascunho';
  // Default tier (was "basic") is now labelled "Free" universally.
  return 'Free';
}

export const DEFAULT_CONFIG = {
  unit: 'mm',
  width: 100,
  height: 50,
  depth: 100,
  thickness: 3,
  sides: 6,
  dimensionType: 'outside',
  openClosed: 'open',
  joint: 'finger',
  fingerSize: 9,
  radius: 30,
  kerf: 0.2,
  cornerCompensation: 0,
  materialPreset: 'mdf3',
  // Cardboard RSC specific
  cardboardThickness: 3,
  glueTab: 20,
  // Luminaire specific
  // Convention: bottomRadius / midRadius / topRadius describe the OUTER
  // edge of the lamp (= rib tip at that height). The actual cavity inside
  // is profile.r − ribWidth.
  profileType: 'vase',
  profilePoints: null, // populated when user enters custom mode
  bottomRadius: 80,
  midRadius: 110,
  topRadius: 60,
  ribCount: 12,
  ribWidth: 30,        // radial width of each slat (mm), adjustable 20–80
  socketDiameter: 42,
  tabWidth: 12,
  bottomDisc: true,
  bottomRingWidth: 25, // width of the open bottom ring (mm); inner hole = 2×(rB − this)
  // Bulb cavity (validated against inner cavity = profile.r − ribWidth)
  bulbDiameter: 60,    // A19/A60 LED standard ~60mm
  bulbHeight: 115,     // overall bulb length below socket flange
  bulbClearance: 15,   // radial safety clearance to material (heat / fitting)
  // Maze / Puzzle
  cols: 20,
  rows: 20,
  cellSize: 8,
  seed: 42,
  entryExit: true,
  randomness: 0.6,
  // Box dividers
  dividerCols: 0,
  dividerRows: 0,
  // ── Ballot box (urna) ─────────────────────────────────────────────
  // Generous default size: ~22 × 28 × 18 cm holds ~A5 ballots folded.
  // The hinge is a knuckle joint with a 5 mm wooden dowel; latch is a
  // tongue + slot with an optional 5 mm padlock/seal hole.
  ballotWidth: 220,
  ballotHeight: 280,
  ballotDepth: 180,
  // Paper drop slot (centered horizontally on the lid; offset toward
  // the back so the ballot doesn't fall on the latch tongue).
  ballotSlotWidth: 140,
  ballotSlotHeight: 6,
  ballotSlotRadius: 3,
  ballotSlotOffsetY: 60,
  // Hinge: number of knuckles on the lid; body gets one less.
  ballotHingeKnuckles: 3,
  ballotHingePinDiameter: 5,
  ballotHingeKnuckleWidth: 24,
  // Latch
  ballotLatchEnabled: true,
  ballotLatchWidth: 25,
  ballotLatchDepth: 9,
  ballotLatchSealHole: true,
  ballotLatchSealDiameter: 5,
};
